import React from "react";
import { Link } from "react-router-dom";
import { 
  Check, ArrowRight, ArrowLeft, Globe, Monitor, Search, 
  Users, Smartphone, Clock, Target, Zap
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import SEOHead from "../components/SEOHead";

const PackVisibilitePage = () => {
  const { t, language } = useLanguage();

  const features = [
    {
      icon: <Monitor className="w-6 h-6" />,
      title: t('packs.visibilityFeature1Title'),
      desc: t('packs.visibilityFeature1Desc')
    },
    {
      icon: <Search className="w-6 h-6" />,
      title: t('packs.visibilityFeature2Title'),
      desc: t('packs.visibilityFeature2Desc')
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: t('packs.visibilityFeature3Title'),
      desc: t('packs.visibilityFeature3Desc')
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: t('packs.visibilityFeature4Title'),
      desc: t('packs.visibilityFeature4Desc')
    }
  ];

  const benefits = [
    t('packs.visibilityBenefit1'),
    t('packs.visibilityBenefit2'),
    t('packs.visibilityBenefit3'),
    t('packs.visibilityBenefit4')
  ];

  return (
    <div className="pt-20">
      <SEOHead 
        title={language === 'fr' 
          ? "Pack Visibilité 360 | ImpaKt Studio - Présence Digitale Complète" 
          : "Visibility 360 Pack | ImpaKt Studio - Complete Digital Presence"
        }
        description={language === 'fr'
          ? "Pack Visibilité 360 à partir de 2 500€. Site web professionnel, SEO, réseaux sociaux. Solution clé en main pour votre présence digitale."
          : "Visibility 360 Pack starting at €2,500. Professional website, SEO, social media. Turnkey solution for your digital presence."
        }
        path="/offres/visibilite-360"
      />
      
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-blue-600 to-cyan-500 overflow-hidden" data-testid="pack-visibilite-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link 
            to="/offres" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
            data-testid="back-to-offres"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('common.backToOffers')}
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-white text-sm font-medium mb-6">
                <Globe className="w-4 h-4" />
                {t('packs.strategicPack')}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                {t('packs.visibilityName')}
              </h1>
              <p className="text-xl text-white/90 mb-4 font-medium">
                {t('packs.visibilityTagline')}
              </p>
              <p className="text-lg text-white/80 mb-8">
                {t('packs.visibilityDesc')}
              </p>
              
              <div className="flex items-baseline gap-2 mb-8">
                <span className="text-white/70 text-lg">{t('common.startingFrom')}</span>
                <span className="text-5xl font-bold text-white">2 500 €</span>
              </div>
              
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 shadow-xl"
                data-testid="cta-visibilite"
              >
                {t('packs.visibilityCta')}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80"
                alt={language === 'fr' ? "Visibilité digitale" : "Digital visibility"}
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">24/7</div>
                    <div className="text-gray-600 text-sm">{t('packs.continueVisibility')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ce qui est inclus */}
      <section className="py-20 bg-white" data-testid="pack-visibilite-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">{t('packs.whatsIncluded')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-4">
              {language === 'fr' ? "Tout ce dont vous avez besoin pour être visible" : "Everything you need to be visible"}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
                data-testid={`feature-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Résultat */}
      <section className="py-20 bg-blue-50" data-testid="pack-visibilite-result">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80"
                alt={language === 'fr' ? "Équipe travaillant sur la visibilité" : "Team working on visibility"}
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
            <div>
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">{t('packs.theResult')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-6">
                {t('packs.visibilityResultTitle')}
              </h2>
              <p className="text-gray-600 text-lg mb-8">
                {t('packs.visibilityResultDesc')}
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-500" data-testid="pack-visibilite-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('packs.visibilityCtaTitle')}
          </h2>
          <p className="text-white/80 text-lg mb-4">
            {t('packs.visibilityName')} {t('common.startingFrom').toLowerCase()} <strong className="text-white">2 500 €</strong>
          </p>
          <p className="text-white/70 mb-10">
            {language === 'fr' ? "Contactez-nous pour un devis personnalisé adapté à vos besoins" : "Contact us for a personalized quote adapted to your needs"}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-600 font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 shadow-lg"
              data-testid="cta-contact-visibilite"
            >
              {t('packs.visibilityCta')}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/offres"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300"
            >
              {t('common.seeAllOffers')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PackVisibilitePage;
